import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMockPortal } from './mock/useMockPortal';
import { saveMockAccount } from './mock/store';
import type { PortalAccountContact } from './mock/types';

const AccountPortal: React.FC = () => {
  const { account } = useMockPortal();
  const [form, setForm] = useState<PortalAccountContact>(account);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(account);
  }, [account]);

  const update =
    (field: keyof PortalAccountContact) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    saveMockAccount(form);
    setSaved(true);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Account
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Update your contact information. Changes stay in this browser session
        only (preview).
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSave}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                fullWidth
                value={form.firstName}
                onChange={update('firstName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                fullWidth
                value={form.lastName}
                onChange={update('lastName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={form.email}
                onChange={update('email')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={form.phone}
                onChange={update('phone')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address line 1"
                fullWidth
                value={form.addressLine1}
                onChange={update('addressLine1')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address line 2"
                fullWidth
                value={form.addressLine2}
                onChange={update('addressLine2')}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="City"
                fullWidth
                value={form.city}
                onChange={update('city')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="State"
                fullWidth
                value={form.state}
                onChange={update('state')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Postal code"
                fullWidth
                value={form.postalCode}
                onChange={update('postalCode')}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
            In production, saving would update your borrower profile after
            verification.
          </Alert>

          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained">
              Save changes
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message="Contact information saved (preview)"
      />
    </Box>
  );
};

export default AccountPortal;
